import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ChatMessage, ChatRequest, ChatResponse } from 'src/app/core/models/ai-chat.model';
import { AiChatService } from 'src/app/core/service/ai-chat.service';

const SESSION_STORAGE_KEY = 'ai_chat_session_id';
const WIDTH_STORAGE_KEY = 'ai_chat_width';
const DEFAULT_WIDTH_PX = Math.max(320, Math.round(window.innerWidth * 0.25));
const MIN_WIDTH = 320;
const MAX_WIDTH = Math.round(window.innerWidth * 0.5);
const WELCOME_MESSAGE = '¡Hola! Soy tu asistente virtual. ¿En qué puedo ayudarte?';

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

@Component({
  selector: 'app-ai-chat',
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.scss'],
})
export class AiChatComponent implements OnInit, OnDestroy {

  @ViewChild('messagesContainer') messagesContainer!: ElementRef<HTMLDivElement>;

  messages: ChatMessage[] = [];
  sessionId = '';
  inputText = '';
  isOpen = false;
  loading = false;
  error: string | null = null;
  panelWidth = DEFAULT_WIDTH_PX;

  private resizing = false;
  private startX = 0;
  private startWidth = 0;

  constructor(private aiChatService: AiChatService) { }

  ngOnInit(): void {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    this.sessionId = stored && stored.trim() ? stored.trim() : generateUUID();
    localStorage.setItem(SESSION_STORAGE_KEY, this.sessionId);

    const storedWidth = localStorage.getItem(WIDTH_STORAGE_KEY);
    if (storedWidth) {
      const width = parseInt(storedWidth, 10);
      if (!isNaN(width)) {
        this.panelWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width));
      }
    }

    this.messages.push({
      role: 'model',
      content: WELCOME_MESSAGE,
    });
  }

  get showWelcome(): boolean {
    return this.messages.length === 1
      && this.messages[0].role === 'model'
      && this.messages[0].content === WELCOME_MESSAGE;
  }

  toggleChat(): void {
    this.isOpen = !this.isOpen;
    this.syncLayoutState();
    if (this.isOpen) {
      setTimeout(() => this.scrollToBottom(), 0);
    }
  }

  private syncLayoutState(): void {
    if (typeof document === 'undefined') {
      return;
    }
    if (this.isOpen) {
      document.body.classList.add('ai-chat-open');
      document.documentElement.style.setProperty('--ai-chat-width', `${this.panelWidth}px`);
    } else {
      document.body.classList.remove('ai-chat-open');
      document.documentElement.style.setProperty('--ai-chat-width', '0px');
    }
  }

  sendMessage(): void {
    const text = this.inputText.trim();
    if (!text || this.loading) {
      return;
    }

    this.inputText = '';
    this.sendTextMessage(text);
  }

  private sendTextMessage(text: string): void {
    if (this.loading) {
      return;
    }

    this.error = null;
    this.messages.push({ role: 'user', content: text });
    this.scrollToBottom();
    this.loading = true;

    const request: ChatRequest = {
      message: text,
      sessionId: this.sessionId,
    };

    this.aiChatService.sendMessage(request).subscribe({
      next: (response: ChatResponse) => {
        const parsed = this.parseMessageContent(
          response.response || 'No obtuve una respuesta, intenta de nuevo.'
        );
        this.messages.push({
          role: 'model',
          content: parsed.text,
          chartData: parsed.chart,
          optionsData: parsed.options,
          downloadData: parsed.download,
        });
        this.loading = false;
        this.scrollToBottom();
      },
      error: (err) => {
        this.loading = false;
        this.error =
          'No se pudo enviar el mensaje. Por favor intenta más tarde.';
        console.error('Error en el chat IA', err);
        this.scrollToBottom();
      },
    });
  }

  parseMessageContent(content: string): {
    text: string;
    chart?: any;
    options?: { options: string[]; message?: string };
    download?: { type: string; filename: string; sheets: { name: string; data: any[][] }[] };
  } {
    if (!content) {
      return { text: content };
    }

    // 1. Intentar extraer desde bloque de código markdown ```json ... ```
    const codeBlockMatch = content.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (codeBlockMatch) {
      try {
        const parsed = JSON.parse(codeBlockMatch[1]);
        const text = content.replace(codeBlockMatch[0], '').trim();
        if (parsed?.chart) {
          return { text, chart: parsed };
        }
        if (Array.isArray(parsed?.chartOptions)) {
          return {
            text,
            options: {
              options: parsed.chartOptions,
              message: parsed.message,
            },
          };
        }
        if (parsed?.download) {
          return { text, download: parsed.download };
        }
      } catch {
        // Ignorar y continuar con detección inline
      }
    }

    // 2. Intentar extraer JSON inline
    const inlineMatch = this.findInlineJson(content);
    if (inlineMatch) {
      try {
        const parsed = JSON.parse(inlineMatch.json);
        const text = (content.slice(0, inlineMatch.start) + content.slice(inlineMatch.end)).trim();
        if (parsed?.chart) {
          return { text, chart: parsed };
        }
        if (Array.isArray(parsed?.chartOptions)) {
          return {
            text,
            options: {
              options: parsed.chartOptions,
              message: parsed.message,
            },
          };
        }
        if (parsed?.download) {
          return { text, download: parsed.download };
        }
      } catch {
        // Ignorar
      }
    }

    return { text: content };
  }

  onChartOptionSelected(type: string): void {
    const label = this.getChartTypeLabel(type);
    const text = `Generame un gráfico de ${label} con esos datos`;
    this.sendTextMessage(text);
  }

  private getChartTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      bar: 'Barras',
      line: 'Línea',
      pie: 'Torta',
      doughnut: 'Dona',
      radar: 'Radar',
      polarArea: 'Área polar',
    };
    return labels[type] || type;
  }

  private findInlineJson(content: string): { json: string; start: number; end: number } | null {
    let start = content.indexOf('{');

    while (start !== -1) {
      let depth = 0;
      let inString = false;
      let escape = false;
      let end = -1;

      for (let i = start; i < content.length; i++) {
        const char = content[i];
        if (escape) {
          escape = false;
          continue;
        }
        if (char === '\\') {
          escape = true;
          continue;
        }
        if (char === '"') {
          inString = !inString;
          continue;
        }
        if (inString) {
          continue;
        }
        if (char === '{') {
          depth++;
        } else if (char === '}') {
          depth--;
          if (depth === 0) {
            end = i + 1;
            break;
          }
        }
      }

      if (end !== -1) {
        const json = content.slice(start, end);
        if (/"chart"/.test(json) || /"chartOptions"/.test(json) || /"download"/.test(json)) {
          return { json, start, end };
        }
      }

      start = content.indexOf('{', start + 1);
    }

    return null;
  }

  clearHistory(): void {
    this.messages = [];
    this.error = null;
    this.sessionId = generateUUID();
    localStorage.setItem(SESSION_STORAGE_KEY, this.sessionId);
    this.messages.push({
      role: 'model',
      content: WELCOME_MESSAGE,
    });
    this.scrollToBottom();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  onResizeStart(event: MouseEvent): void {
    this.resizing = true;
    this.startX = event.clientX;
    this.startWidth = this.panelWidth;
    event.preventDefault();

    document.addEventListener('mousemove', this.onResizeMove);
    document.addEventListener('mouseup', this.onResizeEnd);
  }

  private onResizeMove = (event: MouseEvent): void => {
    if (!this.resizing) {
      return;
    }
    const delta = this.startX - event.clientX;
    const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, this.startWidth + delta));
    this.panelWidth = newWidth;
    this.syncLayoutState();
  };

  private onResizeEnd = (): void => {
    if (!this.resizing) {
      return;
    }
    this.resizing = false;
    localStorage.setItem(WIDTH_STORAGE_KEY, String(this.panelWidth));
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeEnd);
  };

  ngOnDestroy(): void {
    if (typeof document !== 'undefined') {
      document.body.classList.remove('ai-chat-open');
      document.documentElement.style.setProperty('--ai-chat-width', '0px');
    }
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 0);
  }
}
